import type { CommunitySubSector, SourceType } from '@/types';

// The categorical palette — eight colour-blind-safe hues, ported from suit
// (2026-07). Validated so every pair stays distinct under protanopia/
// deuteranopia (worst-pair ΔE 14.9, target ≥12), each hue inside the OKLCH
// working band rather than a washed-out pastel. Square has no positional series
// ramp; the keyed maps below draw their hues from this set. Eight is the ceiling
// — a chart showing more than eight categories can't be fully colour-blind-safe
// on colour alone and leans on labels / tints / the treemap layout.
const qualitativeColors = [
  '#4477B0', // blue
  '#2FA0C0', // cyan
  '#44B089', // green
  '#C98A12', // amber
  '#C85A2B', // rust
  '#C0304A', // crimson
  '#A83E7C', // plum
  '#4E3AA0', // indigo
];

// A muted grey for denominators (metrics / context figures), which are not an
// emission category and should recede rather than read as a series.
const metricGrey = '#8A8D91';

const contextColors = {
  current: '#6FB6C5',
  intermediate: '#2D4F75',
  aggressive: '#6FC58B',
};

const categoryColors = {
  energy: '#2D4F75',
  transport: '#6FB6C5',
  waste: '#2D755B',
  ippu: '#6FC58B',
  agriculture: '#752D49',
  forestry: '#C56FA8',
  development: '#2E3033',
};

// Legacy GPC code palette, keyed by code string. The hues just need to be
// distinct within a chart (no fixed code→colour meaning), so each code family
// cycles the qualitative palette; the "metric" denominator group stays grey.
const colorCodes: any = {
  'data-group-stationary-energy': '#4477B0',
  'data-group-transport': '#2FA0C0',
  'data-group-waste': '#44B089',
  'data-group-ippu': '#C98A12',
  'data-group-agriculture': '#C85A2B',
  'data-group-forestry': '#C0304A',
  'data-group-metric': '#8A8D91',
  'data-type-electricity': '#4477B0',
  'data-type-gas': '#2FA0C0',
  'data-type-lpg': '#44B089',
  'data-type-coal': '#C98A12',
  'data-type-industrial-processes': '#C85A2B',
  'data-type-product-use': '#C0304A',
  'data-type-petrol': '#A83E7C',
  'data-type-diesel': '#4E3AA0',
  'data-type-rail-freight': '#4477B0',
  'data-type-livestock': '#2FA0C0',
  'data-type-horticulture': '#44B089',
  'data-type-waste': '#C98A12',
  'data-type-wastewater': '#C85A2B',
  'data-type-exotic-forestry': '#C0304A',
  'data-type-native-forestry': '#A83E7C',
  'data-type-population': '#4E3AA0',
  'data-type-dwellings': '#4477B0',
  'data-type-employment': '#2FA0C0',
  'sector-residential': '#4477B0',
  'sector-commercial-retail': '#2FA0C0',
  'sector-health-education': '#44B089',
  'sector-manufacturing': '#C98A12',
  'sector-agriculture': '#C85A2B',
  'sector-forestry': '#C0304A',
  'sector-infrastructure': '#A83E7C',
  'sub-sector-multi-unit-dwellings': '#4477B0',
  'sub-sector-single-dwellings': '#2FA0C0',
  'sub-sector-commercial': '#44B089',
  'sub-sector-retail': '#C98A12',
  'sub-sector-education': '#C85A2B',
  'sub-sector-manufacturing': '#C0304A',
  'sub-sector-agriculture': '#A83E7C',
  'sub-sector-forestry': '#4E3AA0',
  'sub-sector-air-travel': '#4477B0',
  'sub-sector-rail-freight': '#2FA0C0',
  'sub-sector-marine-freight': '#44B089',
  'sub-sector-health': '#C98A12',
};

// The Community disclosure's source-type palette. Colour is presentation,
// keyed on the closed `SourceType` enum, and lives in the FE — the server
// ships values, never colours (the disclosure hard rule). Shared by the
// by-source table's bars (Overview) and the "summary by source" treemap.
// The eight emission sources take the eight qualitative hues (a bijection, so
// the set is colour-blind-safe); `community-metrics` stays a muted grey (a
// denominator, not a category) and `solar` keeps its sunny amber.
const sourceColors: Record<SourceType, string> = {
  'stationary-energy': '#4477B0',
  transport: '#2FA0C0',
  waste: '#44B089',
  wastewater: '#4E3AA0',
  water: '#C85A2B',
  agriculture: '#C0304A',
  solar: '#C98A12',
  'community-metrics': metricGrey,
  'industrial-processes': '#A83E7C',
};

// The Community disclosure's "by sector" palette — colour is presentation,
// keyed on the closed `CommunitySubSector` enum, FE-side (the server ships
// values, never colours). Shared by the by-sector table's bars (the Sectors
// breakdown) and the "summary by sector" treemap.
//
// There are 16 sub-sectors but only 8 colour-blind-safe hues, so hues are
// reused across sub-sectors that don't share a chart. `residential` /
// `non-residential` are an independent breakdown (never shown with the rest),
// so they take blue / green regardless of who else uses those hues.
// TODO(colours): de-clash the remaining reused hues against the real
// co-occurring combinations so no two sub-sectors that share a chart share a hue.
const communitySubSectorColors: Record<CommunitySubSector, string> = {
  'multi-unit-dwellings': '#4477B0',
  'single-dwellings': '#2FA0C0',
  commercial: '#44B089',
  retail: '#C98A12',
  education: '#C85A2B',
  manufacturing: '#C0304A',
  agriculture: '#A83E7C',
  forestry: '#4E3AA0',
  'air-travel': '#4477B0',
  'rail-freight': '#2FA0C0',
  'marine-freight': '#44B089',
  health: '#C98A12',
  residential: '#4477B0',
  'non-residential': '#44B089',
  attached: '#A83E7C',
  industrial: '#4E3AA0',
  // CoS fine sub-sectors: apartments are residential (share the residential
  // blue); the rest are non-residential and take distinct palette colours.
  apartments: '#4477B0',
  'accommodation-entertainment': '#C85A2B',
  'commercial-office': '#C98A12',
  community: '#A83E7C',
  'other-non-residential': '#2FA0C0',
};

// Lighten a hex colour toward white by `amount` (0 = unchanged, 1 = white).
// Used by the per-source treemap to give each activity a distinct tint of
// the one source colour (the treemap's leaves are then brightness-varied
// again by Highcharts). Presentation only.
const tint = (hex: string, amount: number): string => {
  const n = parseInt(hex.replace('#', ''), 16);
  const channels = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
  return `#${channels
    .map((c) => Math.round(c + (255 - c) * amount))
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`;
};

export {
  qualitativeColors,
  contextColors,
  categoryColors,
  colorCodes,
  sourceColors,
  communitySubSectorColors,
  tint,
};
